/**
 * O material "adesivo" do DESIGN.md, como componente.
 *
 * Estava escrito no DESIGN.md e implementado à mão no `TelaoLobby`. Com a
 * terceira tela usando a mesma gramática, copiar de novo era garantir que as
 * três divergissem — sombra de 8px numa, 10px na outra, e ninguém saberia qual
 * é a certa. Aqui a regra mora num lugar só.
 *
 * A gramática (DESIGN.md §"O material adesivo"):
 *   1. preenchimento CHAPADO e saturado — zero gradiente
 *   2. contorno em navy, 2–3px, nunca meio-tom
 *   3. sombra dura deslocada, sólida, SEM blur
 *   4. rotação leve só quando é decorativo — alvo torto é alvo mais difícil
 *   5. cantos generosos
 *
 * ⚠️ ONDE NÃO USAR: controle de operação dentro do console. "Botão de 'Começar
 * aula' é botão, não figurinha." O console é registro *product* e precisa sumir
 * dentro da tarefa — quem conduz 25 adolescentes não pode disputar atenção com
 * a própria interface. Aqui vale em título, selo, estado de sucesso, e nas
 * telas que o ALUNO vê no celular.
 */
import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

/** Paleta de marca (src/branding/brands.ts). Navy é sempre a TINTA. */
export const TINTA = '#050b1e';
export const CORES = {
  azul: '#4cc9f0',
  roxo: '#a78bfa',
  teal: '#2ee6a8',
  vermelho: '#e63946',
  branco: '#ffffff',
} as const;

export type CorDeAdesivo = keyof typeof CORES;

/**
 * ⚠️ A TINTA INVERTE EM SUPERFÍCIE ESCURA.
 *
 * O DESIGN.md diz "navy é a tinta", e está certo — mas isso pressupõe o adesivo
 * pousado sobre superfície CLARA, que é o caso da referência (preto sobre cor
 * clara) e do cartão do QR no telão. Direto sobre o fundo navy, contorno e
 * sombra navy simplesmente somem: o adesivo vira um retângulo chapado e a
 * gramática inteira se perde. Só a tela mostra isso — no código parece certo.
 *
 * O `TelaoLobby` já resolvia na marra (sombra azul no elemento que fica sobre o
 * navy). Aqui vira parâmetro: `tinta="clara"` quando o fundo é escuro.
 */
export const TINTA_CLARA = '#ffffff';

interface AdesivoProps extends React.HTMLAttributes<HTMLDivElement> {
  cor?: CorDeAdesivo;
  /** Deslocamento da sombra em px. */
  sombra?: number;
  /** Graus de rotação. SÓ para elemento decorativo — interativo fica em 0. */
  giro?: number;
  /** `escura` (padrão) sobre superfície clara; `clara` sobre fundo navy. */
  tinta?: 'escura' | 'clara';
}

const tintaDe = (t: 'escura' | 'clara') => (t === 'clara' ? TINTA_CLARA : TINTA);

export const Adesivo = forwardRef<HTMLDivElement, AdesivoProps>(
  ({ cor = 'azul', sombra = 6, giro = 0, tinta = 'escura', className, style, children, ...resto }, ref) => (
    <div
      ref={ref}
      className={cn('font-marca rounded-xl border-[3px] px-5 py-3', className)}
      style={{
        backgroundColor: CORES[cor],
        borderColor: tintaDe(tinta),
        // O TEXTO segue navy: ele fica sobre o preenchimento saturado do
        // adesivo, não sobre o fundo da página.
        color: TINTA,
        boxShadow: `${sombra}px ${sombra}px 0 ${tintaDe(tinta)}`,
        transform: giro ? `rotate(${giro}deg)` : undefined,
        ...style,
      }}
      {...resto}
    >
      {children}
    </div>
  ),
);
Adesivo.displayName = 'Adesivo';

/**
 * Botão no material adesivo. A sombra encolhe no toque — a "figurinha" afunda,
 * que é o feedback físico que a referência de 04/08 trouxe.
 *
 * Interativo, então giro é sempre ZERO: alvo torto é alvo mais difícil de
 * acertar, e quem usa isto é adolescente com o celular na mão.
 */
export const BotaoAdesivo = forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    cor?: CorDeAdesivo;
    tinta?: 'escura' | 'clara';
  }
>(({ cor = 'teal', tinta = 'escura', className, style, children, disabled, ...resto }, ref) => (
  <button
    ref={ref}
    disabled={disabled}
    className={cn(
      'font-marca rounded-xl border-[3px] px-6 py-3 text-base font-extrabold uppercase tracking-wide',
      'transition-[box-shadow,transform] duration-100',
      // Afunda no toque: a sombra encolhe junto com o deslocamento, senão o
      // botão parece deslizar em vez de ser pressionado.
      'active:translate-x-[3px] active:translate-y-[3px]',
      // Desabilitado ainda tem que LER como botão. A 50% o adesivo apagava
      // junto com o contorno e a tela abria com a ação principal parecendo
      // quebrada, não "ainda não".
      'disabled:cursor-not-allowed disabled:saturate-[.45] disabled:opacity-80',
      // Foco visível sobre fundo escuro: o anel do tema some no navy.
      'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/70',
      className,
    )}
    style={{
      backgroundColor: CORES[cor],
      borderColor: tintaDe(tinta),
      color: TINTA,
      boxShadow: `6px 6px 0 ${tintaDe(tinta)}`,
      ...style,
    }}
    {...resto}
    // Depois do spread de propósito: antes dele, um onPointerDown vindo de fora
    // apagaria estes calado e o botão perderia o afundar.
    onPointerDown={(e) => {
      e.currentTarget.style.boxShadow = `3px 3px 0 ${tintaDe(tinta)}`;
      resto.onPointerDown?.(e);
    }}
    onPointerUp={(e) => {
      e.currentTarget.style.boxShadow = `6px 6px 0 ${tintaDe(tinta)}`;
      resto.onPointerUp?.(e);
    }}
    onPointerLeave={(e) => {
      e.currentTarget.style.boxShadow = `6px 6px 0 ${tintaDe(tinta)}`;
      resto.onPointerLeave?.(e);
    }}
  >
    {children}
  </button>
));
BotaoAdesivo.displayName = 'BotaoAdesivo';
